import { Link } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import MenuSheet from "@/components/MenuSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  ImagePlus,
  Sparkles,
  Trash2,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  Move,
  Sticker,
  Loader2,
  Plus,
  Pencil,
  Smile,
  Crown,
} from "lucide-react";
import { resizeImageFile } from "@/lib/image";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const BOARD_W = 1200;
const BOARD_H = 800;

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

export default function VisionBoardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [activeBoardId, setActiveBoardId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [newBoardOpen, setNewBoardOpen] = useState(false);
  const [newBoardName, setNewBoardName] = useState("");
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const fileInputRef = useRef(null);
  const boardRef = useRef(null);
  const dragRef = useRef(null);

  // Toast hint the first time the user selects an item this session.
  const hintShownRef = useRef(false);
  useEffect(() => {
    if (!selectedId || hintShownRef.current) return;
    hintShownRef.current = true;
    toast("Selected ✨", {
      description: "Use + to enlarge, − to shrink, arrow keys to nudge.",
      duration: 5000,
    });
  }, [selectedId]);

  // Keyboard shortcuts when an item is selected.
  useEffect(() => {
    if (!selectedId) return;
    const handler = (e) => {
      // Don't intercept when the user is typing in an input/textarea/contenteditable
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      const item = items.find((it) => it.id === selectedId);
      if (!item) return;
      const STEP = e.shiftKey ? 40 : 12;
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        const w = Math.min(1200, item.width + STEP);
        const h = Math.min(1000, item.height + STEP);
        setItems((prev) => prev.map((it) => (it.id === selectedId ? { ...it, width: w, height: h } : it)));
        api.patch(`/vision-items/${selectedId}`, { width: w, height: h }).catch(() => {});
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        const w = Math.max(80, item.width - STEP);
        const h = Math.max(60, item.height - STEP);
        setItems((prev) => prev.map((it) => (it.id === selectedId ? { ...it, width: w, height: h } : it)));
        api.patch(`/vision-items/${selectedId}`, { width: w, height: h }).catch(() => {});
      } else if (e.key === "Escape") {
        setSelectedId(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedId, items]);

  const loadBoards = useCallback(async () => {
    const { data } = await api.get("/vision-boards");
    setBoards(data);
    setActiveBoardId((prev) => {
      if (prev && data.some((b) => b.id === prev)) return prev;
      return data[0]?.id || null;
    });
  }, []);

  const loadItems = useCallback(async (boardId) => {
    if (!boardId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/vision-items", { params: { board_id: boardId } });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBoards();
  }, [loadBoards]);

  useEffect(() => {
    loadItems(activeBoardId);
  }, [activeBoardId, loadItems]);

  const activeBoard = boards.find((b) => b.id === activeBoardId);

  const addBoard = async () => {
    const name = newBoardName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/vision-boards", { name });
      setNewBoardOpen(false);
      setNewBoardName("");
      setBoards((prev) => [...prev, data]);
      setActiveBoardId(data.id);
      toast.success(`Created "${data.name}"`);
    } catch (e) {
      if (e?.response?.status === 402) {
        setNewBoardOpen(false);
        toast.error("Premium feature", {
          description: "Free plan includes 1 vision board. Upgrade to unlock unlimited boards.",
          action: { label: "Upgrade", onClick: () => navigate("/upgrade") },
        });
      } else {
        toast.error("Couldn't create board", {
          description: e?.response?.data?.detail || e.message,
        });
      }
    }
  };

  const renameBoard = async () => {
    if (!activeBoard) return;
    const name = renameDraft.trim();
    if (!name) return;
    await api.patch(`/vision-boards/${activeBoard.id}`, { name });
    setRenameOpen(false);
    loadBoards();
    toast.success("Board renamed");
  };

  const deleteBoard = async () => {
    if (!activeBoard) return;
    await api.delete(`/vision-boards/${activeBoard.id}`);
    toast.success("Board deleted");
    const remaining = boards.filter((b) => b.id !== activeBoard.id);
    setBoards(remaining);
    setActiveBoardId(remaining[0]?.id || null);
    if (remaining.length === 0) loadBoards();
  };

  const onAddImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !activeBoardId) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith("image/")) continue;
        const { dataUrl, width, height } = await resizeImageFile(file, 1400, 0.85);
        const aspect = width / height;
        const targetW = 280;
        const targetH = targetW / aspect;
        const maxZ = items.reduce((m, it) => Math.max(m, it.z_index || 0), 0);
        await api.post("/vision-items", {
          image_data: dataUrl,
          board_id: activeBoardId,
          x: 60 + Math.random() * 200,
          y: 60 + Math.random() * 200,
          width: targetW,
          height: targetH,
          rotation: (Math.random() - 0.5) * 8,
          z_index: maxZ + 1,
        });
      }
      toast.success(`Added ${files.length} image${files.length === 1 ? "" : "s"}`);
      loadItems(activeBoardId);
    } catch {
      toast.error("Could not upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const updateItem = async (id, patch, optimistic = true) => {
    if (optimistic) {
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    }
    try {
      await api.patch(`/vision-items/${id}`, patch);
    } catch {
      loadItems(activeBoardId);
    }
  };

  const removeItem = async (id) => {
    await api.delete(`/vision-items/${id}`);
    setItems((prev) => prev.filter((it) => it.id !== id));
    setSelectedId(null);
  };

  const bringToFront = async (item) => {
    const maxZ = items.reduce((m, it) => Math.max(m, it.z_index || 0), 0);
    if (item.z_index === maxZ) return;
    updateItem(item.id, { z_index: maxZ + 1 });
  };

  const onPointerDownItem = (e, item, mode = "move") => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(item.id);
    if (mode === "move") bringToFront(item);
    const rect = boardRef.current.getBoundingClientRect();
    const scale = rect.width / BOARD_W;
    dragRef.current = {
      id: item.id,
      mode,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: item.x,
      startY: item.y,
      startW: item.width,
      startH: item.height,
      scale,
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = (e.clientX - d.startClientX) / d.scale;
    const dy = (e.clientY - d.startClientY) / d.scale;
    if (d.mode === "move") {
      const x = clamp(d.startX + dx, -d.startW / 2, BOARD_W - d.startW / 2);
      const y = clamp(d.startY + dy, -d.startH / 2, BOARD_H - d.startH / 2);
      setItems((prev) => prev.map((it) => (it.id === d.id ? { ...it, x, y } : it)));
    } else if (d.mode === "resize") {
      // Free aspect: width follows horizontal drag, height follows vertical drag.
      const w = clamp(d.startW + dx, 80, 1200);
      const h = clamp(d.startH + dy, 60, 1000);
      setItems((prev) => prev.map((it) => (it.id === d.id ? { ...it, width: w, height: h } : it)));
    } else if (d.mode === "resize-w") {
      const w = clamp(d.startW + dx, 80, 1200);
      setItems((prev) => prev.map((it) => (it.id === d.id ? { ...it, width: w } : it)));
    } else if (d.mode === "resize-h") {
      const h = clamp(d.startH + dy, 60, 1000);
      setItems((prev) => prev.map((it) => (it.id === d.id ? { ...it, height: h } : it)));
    } else if (d.mode === "rotate") {
      const item = items.find((it) => it.id === d.id);
      if (!item) return;
      const rect = boardRef.current.getBoundingClientRect();
      const cx = rect.left + (item.x + item.width / 2) * d.scale;
      const cy = rect.top + (item.y + item.height / 2) * d.scale;
      const angle = (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI + 90;
      setItems((prev) => prev.map((it) => (it.id === d.id ? { ...it, rotation: angle } : it)));
    }
  };

  const onPointerUp = () => {
    const d = dragRef.current;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    if (d) {
      const item = items.find((it) => it.id === d.id);
      if (item) {
        api.patch(`/vision-items/${d.id}`, {
          x: item.x,
          y: item.y,
          width: item.width,
          height: item.height,
          rotation: item.rotation,
        });
      }
    }
    dragRef.current = null;
  };

  const selected = items.find((it) => it.id === selectedId);

  return (
    <div className="min-h-screen">
      <div className="max-w-[1500px] mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <MenuSheet />
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="rounded-xl"
            data-testid="vision-back-button"
          >
            <Link to="/">
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              Back to planner
            </Link>
          </Button>
        </div>

        {/* Title + Actions */}
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              <Sticker strokeWidth={1.5} className="size-3.5" />
              Vision board
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
              Make it visible
            </h1>
            <p className="text-base text-muted-foreground mt-1">
              Build separate boards for different parts of your life. Drag, resize, rotate.
            </p>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={onAddImage}
              className="hidden"
              data-testid="vision-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !activeBoardId}
              className="rounded-xl h-11"
              data-testid="vision-add-button"
            >
              {uploading ? (
                <Loader2 strokeWidth={1.5} className="size-4 mr-1.5 animate-spin" />
              ) : (
                <ImagePlus strokeWidth={1.5} className="size-4 mr-1.5" />
              )}
              {uploading ? "Uploading…" : "Add images"}
            </Button>
          </div>
        </div>

        {/* Board tabs */}
        <div className="rounded-2xl border border-border bg-card p-2" data-testid="vision-boards-bar">
          <div className="flex items-center gap-2 flex-wrap">
            {boards.map((b) => (
              <button
                key={b.id}
                onClick={() => setActiveBoardId(b.id)}
                data-testid={`vision-board-tab-${b.id}`}
                className={`rounded-xl px-3 py-2 text-sm font-semibold transition-all ${
                  activeBoardId === b.id
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
              >
                {b.name}
              </button>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (!user?.is_premium && boards.length >= 1) {
                  toast("Unlimited vision boards is a Premium feature", {
                    description: "Start your 14-day free trial to add more boards.",
                    action: { label: "Upgrade", onClick: () => navigate("/upgrade") },
                  });
                  return;
                }
                setNewBoardName("");
                setNewBoardOpen(true);
              }}
              className="rounded-xl ml-1"
              data-testid="vision-add-board-button"
            >
              {!user?.is_premium && boards.length >= 1 ? (
                <Crown strokeWidth={1.5} className="size-4 mr-1.5 text-primary" />
              ) : (
                <Plus strokeWidth={1.5} className="size-4 mr-1.5" />
              )}
              {!user?.is_premium && boards.length >= 1
                ? "Add board · Premium"
                : "Add a new vision board"}
            </Button>
            {activeBoard && boards.length > 0 && (
              <div className="ml-auto flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => {
                    setRenameDraft(activeBoard.name);
                    setRenameOpen(true);
                  }}
                  className="rounded-xl size-9"
                  data-testid="vision-rename-board"
                  aria-label="Rename board"
                >
                  <Pencil strokeWidth={1.5} className="size-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="rounded-xl size-9 text-muted-foreground hover:text-destructive"
                      data-testid="vision-delete-board"
                      aria-label="Delete board"
                    >
                      <Trash2 strokeWidth={1.5} className="size-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete "{activeBoard.name}"?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will delete the board and every image on it. This can't be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep it</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={deleteBoard}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete board
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>
        </div>

        {/* New board dialog */}
        <Dialog open={newBoardOpen} onOpenChange={setNewBoardOpen}>
          <DialogContent className="sm:max-w-md rounded-2xl" data-testid="new-board-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">New vision board</DialogTitle>
              <DialogDescription>
                Give it a name — "Travel 2026", "Career", "Wellness"…
              </DialogDescription>
            </DialogHeader>
            <Input
              value={newBoardName}
              onChange={(e) => setNewBoardName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addBoard()}
              placeholder="Board name"
              autoFocus
              data-testid="new-board-name-input"
              className="h-11"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewBoardOpen(false)} className="rounded-xl">
                Cancel
              </Button>
              <Button
                onClick={addBoard}
                disabled={!newBoardName.trim()}
                className="rounded-xl"
                data-testid="new-board-create-button"
              >
                Create board
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rename board dialog */}
        <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Rename board</DialogTitle>
            </DialogHeader>
            <Input
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && renameBoard()}
              autoFocus
              className="h-11"
              data-testid="rename-board-input"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRenameOpen(false)} className="rounded-xl">
                Cancel
              </Button>
              <Button onClick={renameBoard} disabled={!renameDraft.trim()} className="rounded-xl">
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Selected item action bar */}
        {selected && (
          <Card className="rounded-2xl" data-testid="vision-action-bar">
            <CardContent className="p-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mr-2 flex items-center gap-1">
                <Move strokeWidth={1.5} className="size-3.5" />
                Selected
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  updateItem(selected.id, {
                    width: clamp(selected.width * 1.15, 80, 900),
                    height: clamp(selected.height * 1.15, 60, 700),
                  })
                }
                data-testid="vision-zoom-in"
                className="rounded-xl"
              >
                <ZoomIn strokeWidth={1.5} className="size-4 mr-1" /> Bigger
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  updateItem(selected.id, {
                    width: clamp(selected.width * 0.87, 80, 900),
                    height: clamp(selected.height * 0.87, 60, 700),
                  })
                }
                data-testid="vision-zoom-out"
                className="rounded-xl"
              >
                <ZoomOut strokeWidth={1.5} className="size-4 mr-1" /> Smaller
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateItem(selected.id, { rotation: (selected.rotation || 0) - 10 })}
                data-testid="vision-rotate-left"
                className="rounded-xl"
              >
                <RotateCcw strokeWidth={1.5} className="size-4 mr-1" /> Rotate
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => bringToFront(selected)}
                data-testid="vision-bring-front"
                className="rounded-xl"
              >
                <Sparkles strokeWidth={1.5} className="size-4 mr-1" /> Bring to front
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => removeItem(selected.id)}
                data-testid="vision-delete"
                className="rounded-xl text-destructive hover:text-destructive ml-auto"
              >
                <Trash2 strokeWidth={1.5} className="size-4 mr-1" /> Delete
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Canvas */}
        <div
          ref={boardRef}
          onClick={() => setSelectedId(null)}
          className="relative w-full rounded-2xl border-2 border-dashed border-border bg-card shadow-sm overflow-hidden select-none"
          style={{
            aspectRatio: `${BOARD_W}/${BOARD_H}`,
            backgroundImage:
              "radial-gradient(circle at 0% 0%, hsl(var(--primary) / 0.06), transparent 50%), radial-gradient(circle at 100% 100%, hsl(var(--primary) / 0.05), transparent 55%)",
          }}
          data-testid="vision-canvas"
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
              <Loader2 className="size-5 animate-spin mr-2" />
              Loading…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
              <div className="size-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                <Sticker strokeWidth={1.2} className="size-7 text-primary" />
              </div>
              <h3 className="font-display text-2xl font-bold mb-1">
                {activeBoard ? activeBoard.name : "A blank canvas"}
              </h3>
              <p className="text-sm text-muted-foreground max-w-sm mb-5">
                Pick photos from your device and drop them on this board. Drag, resize, rotate — make it your own.
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !activeBoardId}
                className="rounded-xl h-11"
                data-testid="vision-empty-add-button"
              >
                {uploading ? (
                  <Loader2 strokeWidth={1.5} className="size-4 mr-1.5 animate-spin" />
                ) : (
                  <Smile strokeWidth={1.5} className="size-4 mr-1.5" />
                )}
                {uploading ? "Uploading…" : "Add images"}
              </Button>
            </div>
          )}
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`vision-item-${item.id}`}
              onPointerDown={(e) => onPointerDownItem(e, item, "move")}
              className={`absolute touch-none cursor-grab active:cursor-grabbing transition-shadow ${
                selectedId === item.id ? "ring-4 ring-primary/40 shadow-2xl" : "shadow-md"
              }`}
              style={{
                left: `${(item.x / BOARD_W) * 100}%`,
                top: `${(item.y / BOARD_H) * 100}%`,
                width: `${(item.width / BOARD_W) * 100}%`,
                height: `${(item.height / BOARD_H) * 100}%`,
                transform: `rotate(${item.rotation || 0}deg)`,
                transformOrigin: "center",
                zIndex: Math.min(item.z_index || 1, 50),
              }}
            >
              <img
                src={item.image_data}
                alt=""
                draggable={false}
                className="w-full h-full object-cover rounded-xl pointer-events-none"
                style={{ background: "hsl(var(--muted))" }}
              />
              {selectedId === item.id && (
                <>
                  {/* Right edge — widen / narrow */}
                  <div
                    onPointerDown={(e) => onPointerDownItem(e, item, "resize-w")}
                    className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-10 rounded-full bg-primary border-2 border-card cursor-ew-resize"
                    data-testid={`vision-resize-w-${item.id}`}
                    aria-label="Widen or narrow"
                  />
                  {/* Bottom edge — taller / shorter */}
                  <div
                    onPointerDown={(e) => onPointerDownItem(e, item, "resize-h")}
                    className="absolute left-1/2 -bottom-1.5 -translate-x-1/2 h-3 w-10 rounded-full bg-primary border-2 border-card cursor-ns-resize"
                    data-testid={`vision-resize-h-${item.id}`}
                    aria-label="Taller or shorter"
                  />
                  {/* Corner — free both axes */}
                  <div
                    onPointerDown={(e) => onPointerDownItem(e, item, "resize")}
                    className="absolute -bottom-1.5 -right-1.5 size-5 rounded-full bg-primary border-2 border-card cursor-nwse-resize"
                    data-testid={`vision-resize-${item.id}`}
                    aria-label="Resize freely"
                  />
                  <div
                    onPointerDown={(e) => onPointerDownItem(e, item, "rotate")}
                    className="absolute -top-7 left-1/2 -translate-x-1/2 size-5 rounded-full bg-primary border-2 border-card cursor-grab"
                    data-testid={`vision-rotate-handle-${item.id}`}
                  />
                </>
              )}
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground text-center">
          Tap any image to select it. Drag the side dot to widen, the bottom dot to make taller, the corner to resize freely, and the top dot to rotate.
        </p>
      </div>
    </div>
  );
}
